import { Toolbar } from '@mui/material';
import CustomDataIcon from '../Icons/Dashboard/custom-data.svg?react';
import MechanismDataIcon from '../Icons/Dashboard/mechanism-data.svg?react';
import DrivebaseDataIcon from '../Icons/Dashboard/drivebase-data.svg?react';
import MotorDataIcon from '../Icons/Dashboard/motor-data.svg?react';
import SensorDataIcon from '../Icons/Dashboard/sensor-data.svg?react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { projectSlice } from '../../redux/ProjectSlice';
import { ApplicationActions } from '../../utils/constants';
import DrivebaseDataContent from './content/DrivebaseData/DrivebaseDataContent';
import MechanismDataContent from './content/MechanismData/MechanismDataContent';
import CustomDataContent from './content/CustomData/CustomDataContent';
import LeftDrawer, { LeftDrawerTab } from '../Drawers/LeftDrawer';
import { LAYOUT_CONSTANTS } from '../../utils/layout-constants';

interface LeftSideBarProps {
  openWidth: number;
  isOpen: boolean;
}

const DashboardDrawer = ({ openWidth, isOpen }: LeftSideBarProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const savedTab = useAppSelector((state) => state.project.loadedProject?.selectedTabs?.dashboardTab ?? null);

  const { DRIVEBASE_DATA, MOTOR_DATA, SENSOR_DATA, OTHER_DATA, CUSTOM_DATA } = ApplicationActions.Tabs;

  const allTabs = useMemo(
    () => [
      {
        id: 'dashboard-drivebase-data',
        label: t('dashboard.drawer.tab.drivebaseData', 'Drivebase Data'),
        icon: <DrivebaseDataIcon style={{ width: 24, height: 24 }} />,
        value: DRIVEBASE_DATA,
        content: <DrivebaseDataContent />,
      },
      {
        id: 'dashboard-motor-data',
        label: t('dashboard.drawer.tab.motorData', 'Motor Data'),
        icon: <MotorDataIcon style={{ width: 24, height: 24 }} />,
        value: MOTOR_DATA,
        content: <MechanismDataContent electronicsKind="motor" />,
      },
      {
        id: 'dashboard-sensor-data',
        label: t('dashboard.drawer.tab.sensorData', 'Sensor Data'),
        icon: <SensorDataIcon style={{ width: 24, height: 24 }} />,
        value: SENSOR_DATA,
        content: <MechanismDataContent electronicsKind="sensor" />,
      },
      {
        id: 'dashboard-other-data',
        label: t('dashboard.drawer.tab.otherData', 'Other Data'),
        icon: <MechanismDataIcon style={{ width: 24, height: 24 }} />,
        value: OTHER_DATA,
        content: <MechanismDataContent electronicsKind="other" />,
      },
      {
        id: 'dashboard-custom-data',
        label: t('dashboard.drawer.tab.customData', 'Custom Data'),
        icon: <CustomDataIcon style={{ width: 24, height: 24 }} />,
        value: CUSTOM_DATA,
        content: <CustomDataContent />,
      },
    ],
    [DRIVEBASE_DATA, MOTOR_DATA, SENSOR_DATA, OTHER_DATA, CUSTOM_DATA, t]
  );

  const tabs = allTabs;
  const firstTabValue = tabs[0]?.value ?? null;
  const selectedTab = useMemo(() => {
    if (savedTab === null) return null;
    return tabs.some((tab) => tab.value === savedTab) ? savedTab : firstTabValue;
  }, [firstTabValue, savedTab, tabs]);
  const isContentOpen = isOpen && selectedTab !== null;

  const currentTab = useMemo(() => tabs.find((t) => t.value === selectedTab), [tabs, selectedTab]);

  const handleTabClick = useCallback(
    (tabValue: string) => {
      if (isContentOpen && selectedTab === tabValue) {
        dispatch(projectSlice.actions.onSelectDashboardTab(null));
      } else {
        dispatch(projectSlice.actions.onSelectDashboardTab(tabValue));
      }
      if (!isOpen) {
        setTimeout(() => dispatch(projectSlice.actions.toggleDashboardOpenDrawer()), 50);
      }
    },
    [selectedTab, isOpen, isContentOpen, dispatch]
  );

  const handleClose = useCallback(() => {
    dispatch(projectSlice.actions.onSelectDashboardTab(null));
  }, [dispatch]);

  const sharedTabs: LeftDrawerTab[] = tabs.map((t) => ({ id: t.id, label: t.label, icon: t.icon, value: t.value }));

  const handleWidthChange = useCallback(
    (width: number) => {
      dispatch(projectSlice.actions.setLeftDrawerContentWidth({ context: 'dashboard', width }));
    },
    [dispatch]
  );

  return (
    <>
      <Toolbar sx={{ display: 'none' }} />
      <LeftDrawer
        drawerWidth={openWidth}
        isOpen={isContentOpen}
        tabs={sharedTabs}
        selectedTab={selectedTab}
        onTabClick={handleTabClick}
        onClose={handleClose}
        onWidthChange={handleWidthChange}
        minWidth={LAYOUT_CONSTANTS.LEFT_DRAWER_LIMITS.DASHBOARD.MIN}
        maxWidth={LAYOUT_CONSTANTS.LEFT_DRAWER_LIMITS.DASHBOARD.MAX}
        getHeader={(value) => {
          const tab = tabs.find((t) => t.value === value) || currentTab;
          return { icon: tab ? tab.icon : null, label: tab ? tab.label : '' };
        }}
        renderContent={(value) => {
          const tab = tabs.find((t) => t.value === value);
          return tab ? tab.content : null;
        }}
      />
    </>
  );
};

export default DashboardDrawer;
